import { observable } from 'mobx'
import moment from 'moment'

import ReviewModel from '../models/ReviewModel'
import { IReviewModel, ICommunicatorComment, IComment, IScreenshots, IScreenshotBasic } from '../models'
import Communicator from '../Communicator'
import communicate from '../shared/decorators/communicator'
import GenevaWrapper from '../shared/custom/geneva-wrapper'

import { IStores } from '.'

interface Ipayload {
  text: string,
  action?: number,
  screenshotIds?: Array<number>,
}

@communicate(Communicator.getInstance())
export default class ReviewStore {
  store: IStores
  initCommunicator
  @observable collection: Array<any> = []

  @observable current: IReviewModel = null
  @observable hiddenMessageCount: number = 0
  // uses comment before submitting
  @observable activeComment: IComment
  @observable savingImage: boolean
  @observable sendingMessage: boolean

  // to see the store name when in production mode - Uglifyjs
  static storeName = 'ReviewStore'
  storedComment: IComment

  constructor(store: IStores) {
    this.store = store
    this.initCommunicator()
    this.resetActiveComment()
  }

  setCurrent(model: IReviewModel): void {
    this.current = model
  }

  createAndSetCurrent(data: IReviewModel): void {
    this.current = new ReviewModel(this.store, data)
  }

  setHiddenMessageCount(data: IReviewModel) {
    if (data.users[0].lastVisited) {
      const lastVisitedDate = new Date(data.users[0].lastVisited)
      this.current.comments.forEach((comment) => {
        const commentDate = new Date(comment.createdAt)
        if (lastVisitedDate < commentDate) {
          this.hiddenMessageCount++
        }
      })
    }
  }

  resetActiveComment() {
    this.activeComment = {
      action: 0,
      createdAt: null,
      createdBy: null,
      id: null,
      name: null,
      screenshots: [],
      text: '',
      target: ''
    }
  }

  /**
   * Adds a comment from the communicator directly into the UI
   * @param {ICommunicatorComment} data
   */
  addTempComment(data: ICommunicatorComment) {
    // verify that the message is not from the current user
    const resolvedName = data.name ? `${data.name.firstname} ${data.name.lastname}` : 'Unknown'

    // if the sidebar is hidden, update hidden count
    if (!this.store.applicationStore.showSidebar) {
      this.hiddenMessageCount++
    }
    const newLength = this.current.comments[0] && this.current.comments[0].id + 1 || 1

    // avoid double add
    const parsedScreenshot = data.screenshots ? JSON.parse(data.screenshots) : []
    let found = false
    const topComment = this.current.comments[0]
    if (topComment.text === data.text) {
      found = true
    }

    // if the comment had both comments and image, both must match
    if (topComment.screenshots[0] && parsedScreenshot[0]) {
      if (topComment.screenshots[0].id === parsedScreenshot[0].id) {
        found = true
      }
      else {
        found = false
      }
    }

    if (!found) {
      this.current.comments.unshift({
        action: data.action ? data.action * 1 : 0,
        createdAt: moment().toString(),
        createdBy: '',
        id: newLength,
        name: resolvedName,
        text: data.text,
        screenshots: parsedScreenshot,
        target: data.target,
      })

      // notify backend if the message is seen
      if (this.store.applicationStore.showSidebar) {
        this.store.applicationStore.updateLastVisted()
      }
    }
  }

  sendMessage(): void {
    const route = `review/${this.store.applicationStore.pageId}/comment`
    const payload: Ipayload = { text: '' }

    payload.text = this.activeComment.text
    payload.screenshotIds = this.activeComment.screenshots.map(el => el.id)

    if (this.activeComment.action === 1 || this.activeComment.action === 2) {
      payload.action = this.activeComment.action
    }
    this.storedComment = { ...this.activeComment }

    this.sendingMessage = true
    const successCallback = (body: { data: IReviewModel }) => {
      // When successful, then update through comm, just so no one sees invalid messages
      const comm = Communicator.getInstance()
      comm.send({
        action: 'comment',
        room: this.current.id,
        message: this.storedComment.text,
        screenshots: JSON.stringify(this.storedComment.screenshots),
        name: this.store.applicationStore.userName,
        messageAction: this.storedComment.action
      })

      this.createAndSetCurrent(body.data)
      this.sendingMessage = false
    }
    const errorCallback = (err: any) => {
      console.error(err)
      if (err && err.status_code === '403') {
        // eslint-disable-next-line max-len
        alert('Session oder Autorisierung fehlgeschlagen. Bitte laden Sie die Seite neu, um neue Kommentare hinzuzufügen.')
      }
      else {
        // eslint-disable-next-line max-len
        alert('Leider konnte der letzte Kommentar nicht gespeichert werden. Bitte laden Sie die Seite neu und geben Sie den Kommentar erneut ab.')
      }
      this.sendingMessage = false
    }
    GenevaWrapper.post(
      route,
      payload,
      {
        token: this.store.applicationStore.token,
        successCallback,
        errorCallback
      }
    )
  }

  toJS() {
    return this.collection.map(item => item.toJS())
  }

  static fromJS(array, pstore) {
    const store = new ReviewStore(pstore)
    store.collection = array.map(item => ReviewModel.fromJS(pstore, item))
    return store
  }

  createImage(imageData: IScreenshotBasic) {
    const { id } = this.current

    this.savingImage = true
    GenevaWrapper.post(
      `review/${id}/screenshot`,
      imageData,
      {
        token: this.store.applicationStore.token,
        successCallback: (body: { data: IScreenshots }) => {

          // Add the new image to the screenshots array of the model
          this.activeComment.screenshots.push(body.data)
          this.savingImage = false
        },
        errorCallback: (err) => {
          console.warn(err)
          this.savingImage = false
        }
      }
    )
  }

  saveImage(imageId: number, newData: {text: string, imageURL: string}) {
    const { id } = this.current

    GenevaWrapper.put(
      `review/${id}/screenshot/${imageId}`,
      newData,
      {
        token: this.store.applicationStore.token,
        successCallback: (body: { data: IScreenshots }) => {

          // Overwrite the image with the new data
          const existingScreenshot = this.activeComment.screenshots.find(el => el.id === body.data.id)
          Object.assign(existingScreenshot, body.data)
        },
        errorCallback: (err) => {
          console.warn(err)
        }
      }
    )
  }

  deleteImage(imageId: number) {
    const { id } = this.current

    GenevaWrapper.delete(
      `review/${id}/screenshot/${imageId}`,
      {
        token: this.store.applicationStore.token,
        successCallback: () => {

          // Delete the image
          const existingScreenshot = this.activeComment.screenshots.find(el => el.id === imageId)
          const index = this.activeComment.screenshots.indexOf(existingScreenshot)
          this.activeComment.screenshots.splice(index, 1)
        },
        errorCallback: (err) => {
          console.warn(err)
        }
      }
    )
  }
}
